import { valibotResolver } from "@hookform/resolvers/valibot";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import RenderIf from "common/components/RenderIf";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { LinkedInPostSchema } from "common/schemas";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useLinkedInPost from "../../../../datahooks/useLinkedInPost";
import useTargetingMutations from "../../../../datahooks/useTargetingMutations";
import SectionHeading from "../../../SectionHeading";
import StepperNextButton from "../../../StepperNextButton";
import Stepper from "../../Stepper";
import MinerAccountSelector from "../MinerAccountSelector";
import PostDetails from "./PostDetails";

import type { PostStatus, PostTargeting } from "../../../../types";

import PostCampaignImage from "assets/images/post-campaign.png";

export default function Post({
  initialPost,
}: {
  initialPost: PostTargeting | undefined;
}) {
  const { outreach_type: outreachType } = useCampaignContext();
  const isDrip = outreachType === "drip";

  const [postId, setPostId] = useState(initialPost ? initialPost.post_id : "");
  const [postStatus, setPostStatus] = useState<PostStatus>(
    initialPost
      ? {
          isLiked: initialPost.likes,
          isCommented: initialPost.comments,
          isShared: initialPost.reposts,
        }
      : {
          isLiked: true,
          isCommented: true,
          isShared: true,
        },
  );
  const { setTargeting, isSettingTargeting } = useTargetingMutations();
  const { linkedInPost, isLoadingLinkedInPost, isLinkedInPostError } =
    useLinkedInPost(postId);

  const {
    register,
    reset,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: { postUrl: "" },
    resolver: valibotResolver(LinkedInPostSchema),
  });

  function updatePostStatus(status: keyof PostStatus, checked: boolean) {
    setPostStatus(
      produce((draft) => {
        draft[status] = checked;
      }),
    );
  }

  // TODO - Double-check and possibly refactor regexes according to edge cases
  function onInputChange(postUrl: string) {
    // Show post preview if post exists
    setPostId("");
    const postIdMatch = postUrl.match(/\d{19}/);

    if (postIdMatch) {
      setPostId(postIdMatch[0]);
    } else {
      setError("postUrl", { message: "Invalid LinkedIn post URL format" });
    }
  }

  function resetPostUrl() {
    // Reset postUrl state
    reset({ postUrl: "" });

    // Hide post preview
    setPostId("");

    // Reset interactions to false
    setPostStatus({
      isLiked: true,
      isCommented: true,
      isShared: true,
    });
  }

  function goToNextStep() {
    const { isLiked, isCommented, isShared } = postStatus;

    const postDetails = {
      post_id: postId,
      likes: isLiked,
      comments: isCommented,
      reposts: isShared,
    };

    setTargeting({ campaignTargeting: postDetails });
  }

  useEffect(() => {
    if (isLinkedInPostError) {
      reset({ postUrl: "" });
      toast.error(
        "We weren't able to load this preview, please choose a different post",
      );
    }
  }, [isLinkedInPostError]);

  return (
    <>
      <SectionHeading
        title="Post search"
        subtitle="LinkedIn Post campaigns let you target users that have engaged with
        specific LinkedIn posts. They are a great way to find a specific
        audience based on their recent interests and preferred type of content"
      />

      {(postId && !isLinkedInPostError) || isLoadingLinkedInPost ? (
        <PostDetails
          postStatus={postStatus}
          linkedInPost={linkedInPost}
          resetPostUrl={resetPostUrl}
          updatePostStatus={updatePostStatus}
          isLoading={isLoadingLinkedInPost}
        />
      ) : (
        <section className="mx-auto w-fit rounded-5xl lg:border lg:border-black-200 lg:p-5 lg:shadow-md lg:mt-4">
          <img
            className="mb-5 rounded-2xl"
            src={PostCampaignImage}
            alt="Post Campaign"
            width={848}
            height={210}
          />

          <div className="flex flex-col gap-3 lg:flex-row lg:gap-2.5">
            <RenderIf condition={!isDrip}>
              <FormField
                label="Search account"
                htmlFor="miner-account-selector"
              >
                <MinerAccountSelector className="w-40" align="start" />
              </FormField>
            </RenderIf>

            <FormField
              className="w-full"
              label="Post URL"
              htmlFor="postUrl"
              error={
                errors?.postUrl?.message ||
                (isLinkedInPostError &&
                  "Could not find post. Please select a different post.")
              }
              hint=" You can find the URL by clicking the 3 dots at the top of the post in your LinkedIn Feed"
            >
              <Input
                placeholder="Enter post URL here"
                id="postUrl"
                {...register("postUrl", {
                  onChange: (e) => {
                    onInputChange(e.target.value);
                  },
                })}
              />
            </FormField>
          </div>
        </section>
      )}

      <Stepper
        selectedIndex={0}
        nextButtonComponent={
          <StepperNextButton
            disabled={!postId}
            onClick={goToNextStep}
            isLoading={isSettingTargeting}
          />
        }
      />
    </>
  );
}
